/**
 * Hook für Premium-Support-Chat-Zugangsprüfung
 *
 * Kombiniert drei Bedingungen (= 1 von 3 muss erfüllt sein für hasAccess):
 *   1. Admin-Bypass (originalRole === 'admin')
 *   2. Feature-Status 'beta' UND User ist Beta-Tester
 *   3. Feature-Status 'live' UND User hat aktives Abo mit includes_support_chat
 *
 * Wenn Feature-Status 'development': nur Admin-Bypass greift.
 *
 * Drei-Schichten-Sicherheit (siehe Spec §2.4):
 *   - Diese Hook ist Schicht 2 (Frontend-Render-Gate, UX)
 *   - Schicht 1 ist Layout-Gate (Bubble nur in AuthenticatedLayout)
 *   - Schicht 3 ist Backend-Gate (requireChatAccess-Middleware)
 *
 * Sonderfälle:
 *   - Während subscriptions ODER feature-access laden: kein Zugang
 *     (verhindert Aufblitzen der Bubble nach Login, bevor Daten da sind)
 *   - Bubble kann auf jeder Seite gemountet werden — der Layout-Gate
 *     entscheidet, ob sie überhaupt zur Mount-Phase kommt
 */

import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureAccess } from './useFeatureAccess';

export interface ChatAccess {
  /** True wenn User Zugang zum Premium Support hat */
  hasAccess: boolean;
  /** True während Subscription- oder Feature-Daten laden — UI sollte hier nichts rendern */
  isLoading: boolean;
  /** Plan-Slug des aktiven Chat-berechtigten Abos (für Logging/Debug) */
  planSlug: string | null;
  /** Anzeigename des aktiven Plans (z.B. "Bitcoin Whale", für Hinweise) */
  planName: string | null;
}

export function useChatAccess(): ChatAccess {
  const { user, subscriptions, isLoadingSubscriptions, originalRole } = useAuth();
  const featureAccess = useFeatureAccess('premium_support');

  return useMemo<ChatAccess>(() => {
    if (!user) {
      return { hasAccess: false, isLoading: false, planSlug: null, planName: null };
    }

    // Admin-Bypass: Support-Personal MUSS Chat-Zugang haben (sie antworten ja).
    // originalRole prüfen, damit Demo-Modus den Zugang nicht entzieht.
    const effectiveRole = originalRole || user.role;
    if (effectiveRole === 'admin') {
      return { hasAccess: true, isLoading: false, planSlug: 'admin', planName: 'Admin' };
    }

    // Während Daten noch laden: konservativ kein Zugang. Verhindert Aufblitzen.
    if (isLoadingSubscriptions || featureAccess.isLoading) {
      return { hasAccess: false, isLoading: true, planSlug: null, planName: null };
    }

    // Beta-Modus: useFeatureAccess hat den Zugang bereits geprüft (kennt
    // featureStatus + isBetaTester über Backend). Wenn der Hook 'beta'
    // sagt UND hasAccess=true → User ist Beta-Tester eines Beta-Features.
    if (featureAccess.hasAccess && featureAccess.featureStatus === 'beta') {
      // Plan-Check zusätzlich: wenn der User ZUSÄTZLICH einen Plan hat,
      // soll der echte planName angezeigt werden (sieht hochwertiger aus).
      // Sonst: Beta-Marker.
      const chatPlan = subscriptions.find((s) => s.includesSupportChat === true);
      if (chatPlan) {
        return {
          hasAccess: true,
          isLoading: false,
          planSlug: chatPlan.planSlug,
          planName: chatPlan.planName
        };
      }
      return {
        hasAccess: true,
        isLoading: false,
        planSlug: 'beta',
        planName: 'Beta-Programm'
      };
    }

    // Live-Modus: nur User mit aktivem includes_support_chat-Plan
    if (featureAccess.featureStatus === 'live') {
      const chatPlan = subscriptions.find((s) => s.includesSupportChat === true);
      if (chatPlan) {
        return {
          hasAccess: true,
          isLoading: false,
          planSlug: chatPlan.planSlug,
          planName: chatPlan.planName
        };
      }
    }

    // Development oder kein Match: kein Zugang
    return { hasAccess: false, isLoading: false, planSlug: null, planName: null };
  }, [user, subscriptions, isLoadingSubscriptions, originalRole, featureAccess]);
}
